import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { QuizTypesDataService, QuizTypesColumnsService } from '../../services';
import { DialogQuizTypeFormComponent } from './quiz-type-form/quiz-type-form.component';
import { DialogQuizTypeDetailsComponent } from './quiz-type-details/quiz-type-details.component';
import { QuizType, QuizTypesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-quiz-types',
  templateUrl: './quiz-types.component.html',
  styleUrls: ['./quiz-types.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class QuizTypesComponent implements OnInit, OnDestroy {

  private quizTypeAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private quizTypeEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private quizTypeDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public quizTypeToDelete: QuizType = {};
  public quizTypes: QuizType[] = [];
  public quizType: QuizType = {};
  public selectedQuizTypes: QuizType[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private quizTypesDataService: QuizTypesDataService,
    private quizTypesColumnsService: QuizTypesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('QuizTypesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Quiz Types - Management');
    this.primengConfig.ripple = true;
    this.getQuizTypes();
  }

  ngOnDestroy(): void {
    console.log('QuizTypesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getQuizTypes(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.quizTypesDataService.getQuizTypes(this.parameters).subscribe((response: QuizTypesResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.quizTypes = response.results;
        } else {
          this.quizTypes = this.quizTypes.concat(response.results);
        }
        if (response.inlineCount > this.quizTypes.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getQuizTypes();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('QUIZTYPESNOTLOADED') });
    });
  }

  importQuizTypes(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.quizTypesDataService.addQuizTypes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('QUIZTYPESCREATED') });
                if (proceed === j) {
                  this.getQuizTypes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('QUIZTYPESNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('QUIZTYPESNOTCREATED') + ' ' + msg });
            });
          } else {
            this.quizTypesDataService.updateQuizTypes(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('QUIZTYPESUPDATED') });
                if (proceed === j) {
                  this.getQuizTypes();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('QUIZTYPESNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('QUIZTYPESNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.quizTypesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('quizTypes.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedQuizTypes() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Quiz Types?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.quizTypesDataService.deleteQuizTypes(this.selectedQuizTypes).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Quiz Types Deleted', life: 3000 });
            this.getQuizTypes();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Quiz Types not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Quiz Types not deleted' });
        });
        this.selectedQuizTypes = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.quizTypes, this.columns, 'QuizTypes');
  }

  exportExcel() {
    ToolsService.exportExcel(this.quizTypes, this.columns, 'QuizTypes');
  }

  exportCsv() {
    ToolsService.exportCsv(this.quizTypes, this.columns, 'QuizTypes');
  }

  refresh() {
    this.quizTypesDataService.clearCache();
    this.getQuizTypes();
  }

  quizTypeAdd(): void {
    if (!this.navigation.getParamValue('quizTypes.addInDialog', true)) {
      this.router.navigate(['/quiz-types/add']);
      return;
    }
    this.quizTypeAddDialog = this.dialogService.open(DialogQuizTypeFormComponent, {
      header: '', // this.translate.instant('NEWQUIZTYPE'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        quizType: { id: null },
        users: this.quizTypesColumnsService.users,
      },
    });

    this.quizTypeAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'QuizType added' });
        this.getQuizTypes();
      }
    });
  }

  quizTypeEdit(quizType: QuizType): void {
    if (!this.navigation.getParamValue('quizTypes.editInDialog', true)) {
      this.router.navigate(['/quiz-types/edit/' + quizType.id]);
      return;
    }
    this.quizTypeEditDialog = this.dialogService.open(DialogQuizTypeFormComponent, {
      header: '', // this.translate.instant('EDITQUIZTYPE') + ' ' + quizType.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        quizType: quizType,
        users: this.quizTypesColumnsService.users,
      },
    });

    this.quizTypeEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'QuizType updated' });
        this.getQuizTypes();
      }
    });
  }

  quizTypeDetails(quizType: QuizType): void {
    if (!this.navigation.getParamValue('quizTypes.detailsInDialog', true)) {
      this.router.navigate(['/quiz-types/details/' + quizType.id]);
      return;
    }
    this.quizTypeDetailsDialog = this.dialogService.open(DialogQuizTypeDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        quizType: quizType,
      }
    });
  }

  quizTypeDelete(quizType: QuizType): void {
    this.quizTypeToDelete = quizType;
    this.messageService.clear();
    this.messageService.add({
      key: 'quizTypeDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + quizType.name + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.quizTypesDataService.deleteQuizType(this.quizTypeToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Quiz Type deleted' });
      this.getQuizTypes();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Quiz Type not deleted' });
    });
  }

  onDeleteReject(): void {
    this.quizTypeToDelete = {};
    this.messageService.clear('quizTypeDelete');
  }

}
